import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FileUpload from '../components/FileUpload';
import apiService from '../services/api';
import './UploadPage.css';

/**
 * Component for uploading a file and navigating to the validation page after successful upload.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Function} props.setJobId - The function to set the job ID after completing the file upload process.
 * @return {JSX.Element} The rendered UploadPage component.
 */
function UploadPage({ setJobId }) {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const response = await apiService.uploadFile(selectedFile);
      const { job_id } = response.data;

      setJobId(job_id);
      navigate('/validation');
    } catch (error) {
      console.error('Upload error:', error);

      if (error.response && error.response.data && error.response.data.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Failed to upload file. Please try again.');
      }

      setIsUploading(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="upload-page">
      <div className="page-header">
        <button className="back-button" onClick={handleBack}>
          ← Back to Home
        </button>
        <h1>Upload Your Template</h1>
        <p className="page-description">
          Upload your completed spreadsheet before selecting your Thnks and personalizing your message!
        </p>
      </div>

      <div className="upload-content">
        <FileUpload onFileSelect={handleFileSelect} selectedFile={selectedFile} />

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠</span>
            {error}
          </div>
        )}

        <div className="action-buttons">
          <button
            onClick={handleUpload}
            className="btn-primary upload-btn"
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? (
              <>
                <span className="spinner-small"></span>
                Uploading...
              </>
            ) : (
              'Upload & Validate'
            )}
          </button>
        </div>
      </div>

      <div className="help-section">
        <h3>Need Help?</h3>
        <div className="help-items">
          <div className="help-item">
            <span className="help-icon">📋</span>
            <div>
              <h4>Correct Format</h4>
              <p>Use the template from the home page to ensure your data is formatted correctly.</p>
            </div>
          </div>
          <div className="help-item">
            <span className="help-icon">📁</span>
            <div>
              <h4>Supported Files</h4>
              <p>We accept CSV and XLSX file formats up to 10MB.</p>
            </div>
          </div>
          <div className="help-item">
            <span className="help-icon">✅</span>
            <div>
              <h4>Validation Process</h4>
              <p>Addresses are validated using USPS standards for accuracy.</p>
            </div>
          </div>
          <div className="help-item">
            <span className="help-icon">❓</span>
            <div>
              <h4>Need Support</h4>
              <p>Please reach out to <a href="mailto:support@thnks.com">support@thnks.com</a>.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadPage;
